import { createSlice } from "@reduxjs/toolkit";

// ----------------------------------------------------------------------

const initialState = {
  // User data
  email: "",
  userType: "",

  // Flow control
  step: "forgot-password", // 'forgot-password' | 'verify-token' | 'reset-password' | 'success'

  // Token management
  token: "",
  expiresIn: 0, // seconds remaining
  resendCooldown: 0, // seconds until can resend

  // UI states
  loading: false,
  error: null,
  success: false,

  // Form states
  passwordStrength: {
    isValid: false,
    strength: "weak",
    score: 0,
    requirements: {
      minLength: false,
      hasLetter: false,
      hasNumber: false,
      hasSpecialChar: false,
    },
  },
};

const slice = createSlice({
  name: "passwordReset",
  initialState,
  reducers: {
    // Set loading state
    setLoading(state, action) {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },

    // Set error message
    setError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },

    // Clear error
    clearError(state) {
      state.error = null;
    },

    // Set email
    setEmail(state, action) {
      state.email = action.payload.toLowerCase().trim();
    },

    // Set user type from API response
    setUserType(state, action) {
      state.userType = action.payload;
    },

    // Move to next step
    setStep(state, action) {
      state.step = action.payload;
      state.error = null;
    },

    // Handle forgot password success
    forgotPasswordSuccess(state, action) {
      const { email, user_type, expires_in } = action.payload;
      state.email = email;
      state.userType = user_type;
      state.expiresIn = expires_in;
      state.step = "verify-token";
      state.loading = false;
      state.error = null;
      state.resendCooldown = 60; // 60 seconds cooldown for resend
    },

    // Handle verify token success
    verifyTokenSuccess(state, action) {
      const { email, user_type, expires_in } = action.payload;
      state.email = email;
      state.userType = user_type;
      state.expiresIn = expires_in;
      state.step = "reset-password";
      state.loading = false;
      state.error = null;
    },

    // Handle reset password success
    resetPasswordSuccess(state, action) {
      const { email, user_type } = action.payload;
      state.email = email;
      state.userType = user_type;
      state.step = "success";
      state.loading = false;
      state.error = null;
      state.success = true;
    },

    // Set token
    setToken(state, action) {
      state.token = action.payload;
    },

    // Update timer (expires_in countdown)
    updateTimer(state) {
      if (state.expiresIn > 0) {
        state.expiresIn -= 1;
      }
    },

    // Update resend cooldown
    updateResendCooldown(state) {
      if (state.resendCooldown > 0) {
        state.resendCooldown -= 1;
      }
    },

    // Reset resend cooldown
    resetResendCooldown(state) {
      state.resendCooldown = 60;
    },

    // Update password strength
    setPasswordStrength(state, action) {
      state.passwordStrength = action.payload;
    },

    // Reset entire state
    resetState(state) {
      Object.assign(state, initialState);
    },

    // Go back to previous step
    goBack(state) {
      switch (state.step) {
        case "verify-token":
          state.step = "forgot-password";
          break;
        case "reset-password":
          state.step = "verify-token";
          break;
        case "success":
          state.step = "reset-password";
          break;
        default:
          break;
      }
      state.error = null;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  setLoading,
  setError,
  clearError,
  setEmail,
  setUserType,
  setStep,
  forgotPasswordSuccess,
  verifyTokenSuccess,
  resetPasswordSuccess,
  setToken,
  updateTimer,
  updateResendCooldown,
  resetResendCooldown,
  setPasswordStrength,
  resetState,
  goBack,
} = slice.actions;

// Selectors
export const selectPasswordReset = (state) => state.passwordReset;
export const selectPasswordResetLoading = (state) =>
  state.passwordReset.loading;
export const selectPasswordResetError = (state) => state.passwordReset.error;
export const selectPasswordResetStep = (state) => state.passwordReset.step;
export const selectPasswordResetEmail = (state) => state.passwordReset.email;
export const selectPasswordResetUserType = (state) =>
  state.passwordReset.userType;
export const selectPasswordResetToken = (state) => state.passwordReset.token;
export const selectPasswordResetExpiresIn = (state) =>
  state.passwordReset.expiresIn;
export const selectPasswordResetResendCooldown = (state) =>
  state.passwordReset.resendCooldown;
export const selectPasswordStrength = (state) =>
  state.passwordReset.passwordStrength;

